import { FormEvent, useEffect, useState } from "react";
import { toast } from "@/utils/appToast";
import { ResetPasswordForm } from "./ResetPasswordForm";
import { PhoneVerificationForm } from "./PhoneVerificationForm";
import { api } from "@/utils/api";

type ForgotPasswordFormProps = {
  type: "email" | "phone";
  onHide: () => void;
  onStateChange: (showReset: boolean, email?: string, phone?: string) => void;
};

export const ForgotPasswordForm = ({ type, onHide, onStateChange }: ForgotPasswordFormProps) => {
  const [emailSent, setEmailSent] = useState(false);
  const [cooldown, setCooldown] = useState(0); // in seconds
  const [isLoading, setIsLoading] = useState(false);
  const [showResetForm, setShowResetForm] = useState(false);
  const [showVerificationForm, setShowVerificationForm] = useState(false);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  useEffect(() => {
    if (!emailSent || cooldown === 0) return;
    const timer = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [emailSent, cooldown]);

  useEffect(() => {
  // Clear form fields when type changes
  setEmail("");
  setPhone("");
  setEmailSent(false);
  setShowVerificationForm(false);
  setShowResetForm(false);

  // Optional: reset cooldown as well
  setCooldown(0);
}, [type]);


  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const formData = new FormData(e.currentTarget);
      const value = formData.get(type) as string;

      if (!value) {
        toast.error(`Please enter your ${type === "email" ? "email" : "phone number"}`);
        return;
      }

      if (type === "email") {
        const emailInput = formData.get("email") as string;
        setEmail(emailInput);

        try {
          const data = await api.forgotPassword({ type: "email", email: emailInput });
          toast.success("A verification link has been emailed to your email address");
          setEmailSent(true);
          setCooldown(300);
        } catch (err) {
          const message = err?.response?.data?.message || err?.message || "Failed to send verification email";
          toast.error(`Error: ${message}`);
        }
      } else {
        const phone = formData.get("phone") as string;
        const phonePrefix = formData.get("phonePrefix") as string;
        const fullPhone = `+${phonePrefix}${phone}`;

        try {
          const data = await api.forgotPassword({ type: "sms", phone: fullPhone });
          setPhone(fullPhone);
          setShowVerificationForm(true);
          onStateChange(false, undefined, fullPhone);
          toast.success("A verification code has been sent to your phone.");
        } catch (err) {
          const message = err?.response?.data?.message || err?.message || "Failed to send SMS verification code";
          toast.error(`Error: ${message}`);
        }
      }

    } catch (error) {
      const message = error?.response?.data?.message || error?.message || "Unexpected error occurred. Please try again.";
      toast.error(`Error: ${message}`);
    } finally {
      setIsLoading(false);
    }
  };

  if (showResetForm) {
    return <ResetPasswordForm email={email} onHide={onHide} />;
  }

  if (showVerificationForm) {
    return <PhoneVerificationForm phone={phone} onHide={onHide} onVerificationSuccess={() => onStateChange(true)} />;
  }

  const handleResend = async () => {
    try {
      await api.forgotPassword({ type: "email", email });
      toast.success("Verification email resent");
      setCooldown(300);
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || "Failed to resend email";
      toast.error(`Error: ${message}`);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="default-form">
        <div className="form-group">
          <label className="text-white">
            {type === "email" ? "Correo electrónico" : "Número de teléfono"}
          </label>
          {type === "phone" ? (
            <div className="d-flex gap-2">
  <select
    id="phonePrefix"
    name="phonePrefix"
    className="form-select"
    style={{
      flex: 1,
      borderRadius: 0,
      height: '50px',
      fontSize: '16px',
      padding: '10px 12px',
      backgroundColor: '#fff' // Optional: for consistent look
    }}
    required
  >
    <option value="1">+1</option>
    <option value="54">+54</option>
    <option value="92">+92</option>
  </select>
  <input
  type="tel"
  name="phone"
  value={phone}
  onChange={(e) => setPhone(e.target.value)}
  className="form-control"
  placeholder="Introduce tu número de teléfono"
  required
  style={{
    flex: 6,
    borderRadius: 0,
    height: '50px',
    fontSize: '16px',
    padding: '10px 20px'
  }}
/>

</div>

          ) : (
           <input
  type="email"
  name="email"
  value={email}
  onChange={(e) => setEmail(e.target.value)}
  className="form-control"
  placeholder="Introduce tu Correo electrónico"
  required
  disabled={emailSent}
  style={{
    borderRadius: 0,
    height: '50px',
    fontSize: '16px',
    padding: '10px 20px'
  }}
/>
          )}
        </div>

        {!emailSent && (
          <div className="form-group">
            <button
              className="theme-btn btn-style-one w-100"
              type="submit"
              disabled={isLoading}
              style={{ backgroundColor: "#1967d2", border: "none", height: '50px', fontSize: '16px', borderRadius: 14 }}
            >
              {isLoading ? (
                <div className="spinner-border spinner-border-sm" role="status"></div>
              ) : (
                "Continuar"
              )}
            </button>
          </div>
        )}
      </form>

      {emailSent && type === "email" && (
        <div className="text-center text-white mt-3">
          <p className="text-white">¿No ha recibido el correo electrónico?</p>
          <button
            onClick={handleResend}
            disabled={cooldown > 0}
            className="btn btn-link text-decoration-underline"
            style={{ color: "#fff", fontSize: "16px", textDecoration: "underline", padding: 0, opacity: cooldown > 0 ? 0.5 : 1, cursor: cooldown > 0 ? "not-allowed" : "pointer" }}
          >
            {cooldown > 0
              ? `Vuelva a intentarlo en ${Math.floor(cooldown / 60)}:${String(cooldown % 60).padStart(2, "0")}`
              : "Inténtelo de nuevo"}
          </button>
        </div>
      )}
    </>
  );
};
